/**
 * DESIGN TOKENS — the one place a colour, text size, weight or font is defined.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The app-admin counterpart of app-frontend's `src/styles/design-tokens.ts` +
 * `src/variables/_*.scss`. Both portals (CRM `/admin/*` and partner
 * `/partnerAdmin/*`) and the shared `src/components/**` read from here.
 *
 *   palette      colour primitives (see `palette.js` for how they are named)
 *   brand        the active brand: Zenvoya, or the folder laid over it
 *                (`brands/`) — its logos, and the values behind the
 *                brand-dependent `colors.*` groups below
 *   colors       semantic aliases — what a colour is FOR. Prefer these in new code.
 *   partnerBrand the partner's saved brand, as `--pa-brand-*` custom properties
 *   typography   fontFamily / fontSize / fontWeight / lineHeight
 *   radius, shadows
 *   space / spacing(), size, borderWidth, breakpoints — spacing and layout
 *
 * Usage:
 *
 *   import { palette, typography } from 'theme/tokens';
 *   <Box sx={{ color: palette.tw.gray[500], fontSize: typography.fontSize[12],
 *              fontWeight: typography.fontWeight.semiBold }} />
 *
 * CSS / SCSS files cannot import this module; they read the same values as
 * `var(--tk-…)` custom properties, written onto <html> by `applyTokenCssVars`
 * (`cssVars.js`) before the first render.
 *
 * Never write a raw hex, rgba() or font-size literal in a component —
 * `npm run check:tokens` fails on one.
 */
import { palette } from './palette';
import brand from './brands';

export { palette, brand };

// =============================================================================
// SEMANTIC COLOURS — the values the MUI / AntD themes are built from
// =============================================================================

export const colors = {
  brand: {
    ...brand.colors.brand,
    lime: palette.zenvoya.lime,
    sky: palette.zenvoya.sky,
    contrastText: palette.white,
    secondaryContrastText: palette.black,
  },

  text: {
    primary: palette.neutral[870],
    secondary: palette.neutral[670],
    muted: palette.neutral[470],
    disabled: palette.neutral[330],
    inverse: palette.white,
  },

  background: {
    default: palette.neutral[40],
    paper: palette.white,
    subtle: palette.neutral[20],
  },

  border: {
    default: palette.neutral[70],
    strong: palette.neutral[200],
  },

  divider: palette.neutral[70],

  // The CRM's grey ramp, as exposed on `theme.palette.neutral`.
  neutralScale: {
    0: palette.white,
    100: palette.neutral[40],
    200: palette.neutral[70],
    300: palette.neutral[200],
    400: palette.neutral[330],
    500: palette.neutral[470],
    600: palette.neutral[670],
    700: palette.neutral[800],
    800: palette.neutral[900],
    900: palette.black,
  },

  status: {
    success: palette.zenvoya.success,
    error: palette.tw.red[500],
    warning: palette.tw.amber[500],
    info: palette.tw.blue[500],
  },

  // The collapse / expand button on the dark violet sidebar, at rest and on
  // hover — `theme.palette.sidebar` in both portals' MUI themes.
  sidebarChrome: brand.colors.sidebarChrome,

  // Soft accent UI inside pages: selected toggles, accent chips, hover borders.
  accent: brand.colors.accent,

  // The CRM's accent on the shared servicing screens and Booking Detail requests.
  servicingAccent: brand.colors.servicingAccent,

  // A select styled as a pill (partner GST branch picker).
  pill: brand.colors.pill,

  // Translucent partner-console primary (partner antdTheme).
  partnerConsoleAlpha: brand.colors.partnerConsoleAlpha,

  // Both portals' sidebars (admin Sidebar*.jsx, partner AppLayout.jsx).
  sidebar: brand.colors.sidebar,

  // The CRM login page (admin/pages/auth/LoginPage.jsx).
  adminLogin: brand.colors.adminLogin,

  // The partner login before a partner code is recognised (partnerThemes.js).
  partnerLogin: brand.colors.partnerLogin,

  // What a partner who has saved no branding gets (partner/theme/brandTokens.js).
  partnerDefaultBranding: brand.colors.partnerDefaultBranding,

  // The partner console's AntD chrome (antdTheme.js). Deliberately NOT branded:
  // the sidebar stays this panel on every tenant (see AppLayout.jsx).
  partnerConsole: {
    primary: brand.colors.partnerConsolePrimary,
    success: palette.tw.emerald[500],
    textBase: palette.custom.indigo[830],
    textSecondary: palette.tw.gray[500],
    border: palette.tw.gray[200],
    borderSecondary: palette.tw.gray[100],
    layoutBg: palette.custom.blue[30],
    siderBg: palette.custom.indigo[830],
    subMenuBg: palette.custom.indigo[880],
    tableHeaderBg: palette.custom.sky[20],
    rowHoverBg: palette.tw.gray[50],
  },
};

// =============================================================================
// PARTNER BRAND — the partner's saved colours, live
// =============================================================================
//
// `applyBrandTokens` (partner/theme/brandTokens.js) writes `--pa-brand-*` onto
// <html> from the branding the partner saved. These carry the pre-branding
// literal as the fallback, so a screen painted before the first write — or
// outside the partner portal — looks exactly as it did.

export const partnerBrand = {
  primary: `var(--pa-brand-primary, ${brand.colors.partnerFallback.primary})`,
  primaryLight: `var(--pa-brand-primary-light, ${brand.colors.partnerFallback.primaryLight})`,
  secondary: `var(--pa-brand-secondary, ${brand.colors.partnerFallback.secondary})`,
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  fontFamily: {
    inter: "'Inter'",
    interSans: "'Inter', sans-serif",
    interSystem: "'Inter', system-ui, -apple-system, sans-serif",
    mono: 'monospace',
    monoSystem: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    monoCode: "'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace",
  },

  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },

  /**
   * Keyed by the rendered size in px (at the browser's 16px root); the value is
   * rem so text still follows a user's browser font-size setting.
   * `fontSize[12]` === '0.75rem'.
   *
   * The everyday steps are 10 / 12 / 14 / 16 / 18 / 20 / 24. The fractional keys
   * (11.2, 13.12 …) are sizes individual screens already used — kept exact so
   * nothing moved — and are candidates to fold into the nearest step.
   */
  fontSize: {
    8: '0.5rem',
    9: '0.5625rem',
    10: '0.625rem',
    11: '0.6875rem',
    '11.2': '0.7rem',
    '11.52': '0.72rem',
    12: '0.75rem',
    '12.48': '0.78rem',
    '12.8': '0.8rem',
    13: '0.8125rem',
    '13.12': '0.82rem',
    '13.6': '0.85rem',
    14: '0.875rem',
    '14.4': '0.9rem',
    '14.72': '0.92rem',
    15: '0.9375rem',
    16: '1rem',
    17: '1.0625rem',
    '17.6': '1.1rem',
    18: '1.125rem',
    20: '1.25rem',
    '20.8': '1.3rem',
    22: '1.375rem',
    24: '1.5rem',
    26: '1.625rem',
    28: '1.75rem',
    '29.6': '1.85rem',
    30: '1.875rem',
    32: '2rem',
    40: '2.5rem',
    44: '2.75rem',
    48: '3rem',
    56: '3.5rem',
    64: '4rem',
    96: '6rem',
  },

  /**
   * The same scale as plain numbers, for the consumers that refuse a CSS string:
   * AntD theme tokens (its algorithm does arithmetic on them) and MUI's root
   * `typography.fontSize`.
   */
  fontSizePx: {
    10: 10,
    12: 12,
    14: 14,
    16: 16,
    18: 18,
    20: 20,
    22: 22,
    24: 24,
  },

  lineHeight: {
    none: 1,
    tight: 1.2,
    snug: 1.3,
    normal: 1.4,
    relaxed: 1.5,
    loose: 1.6,
    looser: 1.7,
  },

  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    /** Small uppercase labels (Theme Studio section labels). */
    wide: '0.04em',
  },
};

// =============================================================================
// RADIUS (px numbers — MUI multiplies a bare number in `sx` by shape.borderRadius,
// so use these in theme overrides / `style`, or as `${radius.md}px` in `sx`)
// =============================================================================

export const radius = {
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 14,
  xxxl: 16,
  pill: 999,
  circle: '50%',
};

// =============================================================================
// SPACING & LAYOUT (px numbers, keyed by their px value like `typography.fontSize`)
//
// MUI's numeric `sx` spacing (`p: 2`, `mb: 1.5`, `gap: 1`) and numeric `sx`
// `borderRadius` are ALREADY tokens — MUI multiplies them by `theme.spacing` /
// `theme.shape.borderRadius` — so they stay as written. Everything else (an AntD
// `style={{}}`, a px string, a fixed width) takes a value from here:
//
//   style={{ padding: space[16] }}            sx={{ padding: spacing(12, 16) }}
//   sx={{ width: size[120] }}                 border: `${borderWidth.thin} solid …`
//
// Steps are even by rule; a value missing here is added here, never typed inline.
// Relative units (`'100%'`, `'50%'`, `vh`, `vw`, `'auto'`) are not fixed sizes
// and are written as they are.
// =============================================================================

export const space = {
  0: 0, 2: 2, 4: 4, 6: 6, 8: 8, 10: 10, 12: 12, 14: 14, 16: 16, 18: 18, 20: 20,
  24: 24, 26: 26, 28: 28, 30: 30, 32: 32, 36: 36, 40: 40, 44: 44, 48: 48, 56: 56, 64: 64,
};

/**
 * Space shorthand as a CSS string: `spacing(12, 16)` → `'12px 16px'`,
 * `spacing(0, 'auto')` → `'0 auto'`. An off-scale number still renders, but warns
 * outside production — add the step to `space` instead.
 */
export const spacing = (...steps) =>
  steps
    .map((s) => {
      if (typeof s !== 'number') return s;
      if (space[s] === undefined && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[tokens] spacing(${s}) is not on the space scale`);
      }
      return s === 0 ? '0' : `${s}px`;
    })
    .join(' ');

/** Fixed dimensions — widths, heights, icon/avatar sizes, offsets — in px. */
export const size = {
  2: 2, 3: 3, 4: 4, 6: 6, 8: 8, 10: 10, 12: 12, 14: 14, 16: 16, 18: 18, 20: 20, 22: 22,
  24: 24, 26: 26, 28: 28, 32: 32, 34: 34, 36: 36, 38: 38, 40: 40, 42: 42, 44: 44,
  46: 46, 48: 48, 50: 50, 52: 52, 54: 54, 56: 56, 58: 58, 60: 60, 64: 64, 68: 68,
  70: 70, 72: 72, 80: 80, 88: 88, 90: 90, 96: 96, 100: 100, 110: 110, 116: 116,
  120: 120, 122: 122, 128: 128, 130: 130, 140: 140, 150: 150, 160: 160, 170: 170,
  180: 180, 200: 200, 220: 220, 240: 240, 250: 250, 260: 260, 264: 264, 280: 280, 300: 300,
  320: 320, 400: 400, 420: 420, 440: 440, 490: 490, 500: 500, 520: 520, 560: 560,
  596: 596, 600: 600, 640: 640, 680: 680, 720: 720, 800: 800, 820: 820, 860: 860,
  900: 900, 1100: 1100, 1200: 1200, 1300: 1300, 1400: 1400, 1440: 1440, 1500: 1500, 1700: 1700,
};

export const borderWidth = {
  thin: '1px',
  medium: '2px',
  thick: '4px',
};

/**
 * Viewport breakpoints for JS media queries / `matchMedia`. CSS `@media` cannot
 * read a custom property, so stylesheets keep these numbers in their queries.
 */
export const breakpoints = {
  mobileMax: 767.98,
  tablet: 768,
  tabletMax: 1023.98,
  desktop: 1024,
  wide: 1366,
};

// =============================================================================
// SHADOWS — the elevation scale behind MUI's `theme.shadows`
// =============================================================================

export const shadows = {
  xxSmall: `0px 1px 2px ${palette.alpha.black[5]}`,
  xSmall: `0px 1px 3px ${palette.alpha.black[10]}, 0px 1px 2px ${palette.alpha.black[6]}`,
  small: `0px 4px 6px -1px ${palette.alpha.black[10]}, 0px 2px 4px -1px ${palette.alpha.black[6]}`,
  medium: `0px 10px 15px -3px ${palette.alpha.black[10]}, 0px 4px 6px -2px ${palette.alpha.black[5]}`,
  large: `0px 20px 25px -5px ${palette.alpha.black[10]}, 0px 10px 10px -5px ${palette.alpha.black[4]}`,
  xLarge: `0px 25px 50px -12px ${palette.alpha.black[25]}`,
  xxLarge: `0px 35px 60px -15px ${palette.alpha.black[30]}`,
};

export const tokens = { palette, brand, colors, partnerBrand, typography, radius, space, spacing, size, borderWidth, breakpoints, shadows };

export default tokens;
